import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Barra de voltar. STICKY, em toda tela que não é uma das quatro da navbar.
 *
 * Isso não é estética: no PWA instalado não existe gesto de voltar do
 * navegador. Se a barra rola para fora da tela, a pessoa fica presa e o
 * único caminho é fechar o app. Por isso é `sticky top-0` e não some nunca.
 *
 * `sobreFoto`: a barra começa transparente, POR CIMA da foto de capa, com
 * os botões em círculos escuros (referência: Dribbble 18972763 / 27589455),
 * e vira barra sólida com título assim que a foto rola para fora. A mesma
 * saída, nos dois estados.
 */
export function BarraVoltar({
  titulo,
  acaoDireita,
  onVoltar,
  sobreFoto = false,
  limiar = 200,
}: {
  /** Sem título a barra ainda existe: o que ela garante é a saída, não o texto. */
  titulo?: string;
  /** Nó à direita; em função, recebe se a barra está flutuando sobre a foto. */
  acaoDireita?: React.ReactNode | ((flutuando: boolean) => React.ReactNode);
  onVoltar?: () => void;
  /** Começa transparente por cima da foto de capa. */
  sobreFoto?: boolean;
  /** Rolagem (px) a partir da qual a barra vira sólida, no modo sobreFoto. */
  limiar?: number;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rolou, setRolou] = useState(false);

  useEffect(() => {
    if (!sobreFoto) return;
    let pedido = 0;
    const ler = () => {
      pedido = 0;
      setRolou(window.scrollY > limiar);
    };
    const aoRolar = () => {
      if (!pedido) pedido = requestAnimationFrame(ler);
    };
    ler();
    window.addEventListener("scroll", aoRolar, { passive: true });
    return () => {
      window.removeEventListener("scroll", aoRolar);
      if (pedido) cancelAnimationFrame(pedido);
    };
  }, [sobreFoto, limiar]);

  const voltar = () => {
    if (onVoltar) return onVoltar();
    if (window.history.length > 2) return navigate(-1);
    navigate("/");
  };

  const flutuando = sobreFoto && !rolou;

  return (
    <div
      className={`sticky top-0 z-30 ${flutuando ? "" : "border-b border-border bg-background"}`}
      style={{
        marginBottom: sobreFoto ? "calc(-1 * var(--barra-voltar))" : undefined,
        transition: "background-color 200ms",
      }}
      data-flutuando={flutuando ? "1" : "0"}
    >
      <div
        className="flex items-center gap-3"
        style={{
          minHeight: "var(--barra-voltar)",
          paddingLeft: "12px",
          paddingRight: "12px",
        }}
      >
        <button
          type="button"
          onClick={voltar}
          aria-label={t("common.back")}
          className={`toque flex shrink-0 items-center justify-center rounded-full ${flutuando ? "botao-sobre-foto" : "text-foreground active:bg-secondary"}`}
          style={{ width: "var(--alvo-min)", height: "var(--alvo-min)" }}
        >
          <ArrowLeft className="h-7 w-7" strokeWidth={2.5} />
        </button>
        <h1
          className="min-w-0 flex-1 truncate font-bold leading-tight"
          style={{ fontSize: "var(--t-linha)", opacity: flutuando ? 0 : 1, transition: "opacity 200ms" }}
        >
          {titulo ?? ""}
        </h1>
        {acaoDireita && (
          <div className="shrink-0">{typeof acaoDireita === "function" ? acaoDireita(flutuando) : acaoDireita}</div>
        )}
      </div>
    </div>
  );
}
